import {createAsyncThunk} from '@reduxjs/toolkit';
import {formatPrice} from 'utils/number-format';
import {
    getFacilityRateThresholds,
    updateFacilityRateThresholds,
} from 'api/rates/rate-thresholds';
import getFacilityDetails from 'api/facilities/get-facility-details';
import {rateThresholdSchema} from '../utils/validationSchemas';
import {rateThresholdsAdapter} from './spothero-iq-adapters';

/** @namespace SpotheroIQ.Actions */

/**
 * Thunk that gets the rate thresholds and adds them to the redux store
 * @function getRateThresholds
 * @param {number} facilityId - The id of the facility
 *
 */
export const getRateThresholds = createAsyncThunk(
    'spotheroIQ/getRateThresholds',
    async ({facilityId}, {rejectWithValue}) => {
        try {
            const [rateThresholds, {areRateThresholdsApplicable}] =
                await Promise.all([
                    getFacilityRateThresholds({
                        facilityId,
                    }),
                    getFacilityDetails(facilityId),
                ]);

            return {
                rateThresholds,
                areRateThresholdsApplicable,
            };
        } catch (error) {
            const errorMessage =
                error?.data?.data?.errors?.[0]?.messages?.[0] || error.message;

            return rejectWithValue(errorMessage);
        }
    }
);

/**
 * Thunk that updates the rate thresholds in the redux store
 * @function updateRateThreshold
 * @param {number} facilityId - The id of the facility to update the rate thresholds for
 * @param {Object<{rateId: number, value: number}>} rateThreshold - The rate threshold to update
 *
 */
export const updateRateThreshold = createAsyncThunk(
    'spotheroIQ/updateRateThreshold',
    async ({facilityId, rateThreshold}, {getState, rejectWithValue}) => {
        try {
            // create the collection we'll send to the API
            const nextState = rateThresholdsAdapter.upsertOne(
                getState().spotheroIQ.rateThresholds,
                {
                    id: rateThreshold.rateId,
                    threshold: formatPrice(rateThreshold.value, {
                        formatterCallback: Number,
                        parseValue: true,
                    }),
                }
            );

            // Get the updated rate thresholds collection from the normalized entityAdapter
            const rateThresholds = rateThresholdsAdapter
                .getSelectors()
                .selectAll(nextState);

            // now we can run the validation on the rate thresholds collection
            const validatedRateThresholds =
                await rateThresholdSchema.validate(rateThresholds);

            const response = await updateFacilityRateThresholds({
                facilityId,
                rateThresholds: validatedRateThresholds,
            });

            return response;
        } catch (error) {
            const errorMessage =
                error?.data?.data?.errors[0]?.messages[0] || error.message;

            return rejectWithValue(errorMessage);
        }
    }
);

/**
 * Thunk that deletes a rate threshold in the redux store
 * @function deleteRateThreshold
 * @param {number} facilityId - The id of the facility to update the rate thresholds for
 * @param {Object<{rateId: number}>} rateThreshold - The rate threshold to delete
 *
 */
export const deleteRateThreshold = createAsyncThunk(
    'spotheroIQ/deleteRateThreshold',
    async ({facilityId, rateThreshold}, {getState, rejectWithValue}) => {
        try {
            // create the collection we'll send to the API
            const nextState = rateThresholdsAdapter.removeOne(
                getState().spotheroIQ.rateThresholds,
                rateThreshold.rateId
            );

            // Get the updated rate thresholds collection from the normalized entityAdapter
            const rateThresholds = rateThresholdsAdapter
                .getSelectors()
                .selectAll(nextState);

            const response = await updateFacilityRateThresholds({
                facilityId,
                rateThresholds,
            });

            return response;
        } catch (error) {
            const errorMessage =
                error?.data?.data?.errors[0]?.messages[0] || error.message;

            return rejectWithValue(errorMessage);
        }
    }
);
